import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout.jsx'
import Login from './routes/Login.jsx'
import Register from './routes/Register.jsx'
import ForgotPassword from './routes/ForgotPassword.jsx'
import ResetPassword from './routes/ResetPassword.jsx'
import AcceptInvite from './routes/AcceptInvite.jsx'
import AcceptStaffInvite from './routes/AcceptStaffInvite.jsx'
import PrintEncounter from './routes/PrintEncounter.jsx'
import RoleHome from './routes/RoleHome.jsx'
import Unauthorized from './routes/Unauthorized.jsx'
import NotFound from './routes/NotFound.jsx'
import Health from './routes/Health.jsx'
import RequireAuth from './auth/RequireAuth.jsx'
import RequireRole from './auth/RequireRole.jsx'
import { ROLES, ROLE_GROUPS } from './constants/index.js'

import DoctorDashboard from './features/doctor/DoctorDashboard.jsx'
// EncounterWorkspace (the older tabbed editor) was retired in favour of
// EncounterEditor — a single-page CNE document with no tabs and no
// narrative templates. The old file is dead code, kept on disk only
// because the workspace tooling can't delete it from this sandbox.
import EncounterEditor from './features/doctor/EncounterEditor.jsx'
import PatientDetail from './features/doctor/PatientDetail.jsx'
import DoctorScreeningCampaignDetail from './features/doctor/DoctorScreening.jsx'

import LabDashboard from './features/laboratory/LabDashboard.jsx'
import LabOrderDetail from './features/laboratory/LabOrderDetail.jsx'
import LaboratoryCatalogue from './features/laboratory/LaboratoryCatalogue.jsx'

import PatientHome from './features/patient/PatientHome.jsx'
import PatientOtcBrowser from './features/patient/PatientOtcBrowser.jsx'
import PatientLabBrowser from './features/patient/PatientLabBrowser.jsx'
import PatientImagingBrowser from './features/patient/PatientImagingBrowser.jsx'
import PatientAmbulanceBrowser from './features/patient/PatientAmbulanceBrowser.jsx'

import PharmacyDashboard from './features/pharmacy/PharmacyDashboard.jsx'
import GlobalChat from "./components/GlobalChat.jsx";
import PharmacyOrderDetail from './features/pharmacy/PharmacyOrderDetail.jsx'
import PharmacyCatalogue from './features/pharmacy/PharmacyCatalogue.jsx'

import RadiologyDashboard from './features/radiology/RadiologyDashboard.jsx'
import RadiologyOrderDetail from './features/radiology/RadiologyOrderDetail.jsx'
import RadiologyCatalogue from './features/radiology/RadiologyCatalogue.jsx'

import AmbulanceDashboard from './features/ambulance/AmbulanceDashboard.jsx'
import AmbulanceRequestDetail from './features/ambulance/AmbulanceRequestDetail.jsx'
import AmbulanceCatalogue from './features/ambulance/AmbulanceCatalogue.jsx'
import VisitServiceEntry from './features/doctor/VisitServiceEntry.jsx'

import InvitationsPage from './features/admin/InvitationsPage.jsx'
import StaffInvitationsPage from './features/admin/StaffInvitationsPage.jsx'
import SettingsPage from './features/settings/SettingsPage.jsx'
import AvailabilityPage from './features/doctor/AvailabilityPage.jsx'
import BookConsultation from './features/patient/BookConsultation.jsx'
import MyBookings from './features/patient/MyBookings.jsx'

import OrgDashboard from './features/organization/OrgDashboard.jsx'
import NationalDashboard from './features/organization/NationalDashboard.jsx'
import NationalHealthIntelligence from './features/national/NationalHealthIntelligence.jsx'
import AuditLogViewer from './features/audit/AuditLogViewer.jsx'
import PlatformCostsPage from './features/admin/PlatformCostsPage.jsx'
import ServiceCoverageConfigPage from './features/admin/ServiceCoverageConfigPage.jsx'
import OrgStatementsPage from './features/admin/OrgStatementsPage.jsx'
import ScreeningPage from './features/admin/ScreeningPage.jsx'
import ScreeningCampaignDetail from './features/admin/ScreeningCampaignDetail.jsx'
import TriageQueuePage from './features/admin/TriageQueuePage.jsx'
import OperationsDashboard from './features/admin/OperationsDashboard.jsx'

export default function App() {
  return (
   <>
    <Routes>
      {/* public */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset/:uid/:token" element={<ResetPassword />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      {/* Public accept-invite for the polymorphic staff flow (pharmacy /
          lab / radiology / ambulance / org_admin). Distinct path from the
          doctor flow above so each consumes the right backend endpoint
          and shows the right copy on the shell. */}
      <Route path="/accept-invite/staff/:token" element={<AcceptStaffInvite />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/health" element={<Health />} />

      {/* authenticated area */}
      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          <Route path="/" element={<RoleHome />} />

          {/* Settings — universal across every authenticated role.
              Lives outside any RequireRole guard so doctors, patients,
              all staff types, and org admins use the same screen to
              edit their profile + change their password. */}
          <Route path="/settings" element={<SettingsPage />} />

          {/* Doctor / Specialist share the encounter workspace routes for now */}
          <Route element={<RequireRole roles={ROLE_GROUPS.CLINICIAN} />}>
            <Route path="/doctor" element={<DoctorDashboard />} />
            <Route path="/doctor/patients/:patientId" element={<PatientDetail />} />
            <Route path="/doctor/encounters/:encounterId" element={<EncounterEditor />} />
            <Route path="/doctor/encounters/:encounterId/print" element={<PrintEncounter />} />
            {/* Doctor-facing routine medical screening. The campaign list
                is a tab on the doctor dashboard; this is the per-campaign
                working surface (intake + fitness outcomes). Campaign /
                protocol management and the cohort report stay admin-only
                on /superuser/screening. */}
            <Route path="/doctor/screening/campaigns/:id" element={<DoctorScreeningCampaignDetail />} />
            {/* Slot-by-slot availability editor — drives the patient
                booking flow. 15-min granularity, drag-toggle grid. */}
            <Route path="/doctor/availability" element={<AvailabilityPage />} />
            <Route path="/doctor/visits" element={<VisitServiceEntry />} />
          </Route>

          {/* Laboratory */}
          <Route element={<RequireRole roles={[ROLES.LABORATORY]} />}>
            <Route path="/lab" element={<LabDashboard />} />
            <Route path="/lab/orders/:orderId" element={<LabOrderDetail />} />
            <Route path="/lab/catalogue" element={<LaboratoryCatalogue />} />
          </Route>
          {/* Admin can also reach the lab catalogue page directly. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/lab-catalogue" element={<LaboratoryCatalogue />} />
          </Route>

          {/* Patient — read + self-upload dashboard */}
          <Route element={<RequireRole roles={[ROLES.PATIENT]} />}>
            <Route path="/patient" element={<PatientHome />} />
            <Route path="/patient/otc" element={<PatientOtcBrowser />} />
            {/* Service marketplace — discover lab tests, imaging studies,
                and ambulance services across the Wantok network. Lab and
                imaging are browse-only (orders flow through a doctor);
                ambulance is a directory + tel: dial-out CTA. */}
            <Route path="/patient/labs" element={<PatientLabBrowser />} />
            <Route path="/patient/imaging" element={<PatientImagingBrowser />} />
            <Route path="/patient/ambulance" element={<PatientAmbulanceBrowser />} />
            {/* Booking flow — discover doctor → pick slot → confirm.
                Uses slot-by-slot availability authored on the doctor side. */}
            <Route path="/patient/book" element={<BookConsultation />} />
            <Route path="/patient/bookings" element={<MyBookings />} />
          </Route>

          {/* Pharmacy */}
          <Route element={<RequireRole roles={[ROLES.PHARMACY]} />}>
            <Route path="/pharmacy" element={<PharmacyDashboard />} />
            <Route path="/pharmacy/orders/:orderId" element={<PharmacyOrderDetail />} />
            <Route path="/pharmacy/catalogue" element={<PharmacyCatalogue />} />
          </Route>
          {/* Platform admin can manage any pharmacy's catalogue. The
              prefix is /superuser/ — NOT /admin/, which is Django's
              backend admin and would 404 the SPA. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/pharmacy-catalogue" element={<PharmacyCatalogue />} />
          </Route>

          {/* Radiology */}
          <Route element={<RequireRole roles={[ROLES.RADIOLOGY]} />}>
            <Route path="/radiology" element={<RadiologyDashboard />} />
            <Route path="/radiology/orders/:orderId" element={<RadiologyOrderDetail />} />
            <Route path="/radiology/catalogue" element={<RadiologyCatalogue />} />
          </Route>
          {/* Admin can also reach the radiology catalogue page directly. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/radiology-catalogue" element={<RadiologyCatalogue />} />
          </Route>

          {/* Subscribing-organization admin dashboard. Org admins reach
              /org/dashboard (auto-scoped); platform admins use
              /superuser/org-dashboard with a pharmacy dropdown. */}
          <Route>
            <Route path="/org/dashboard" element={<OrgDashboard />} />
            <Route path="/superuser/org-dashboard" element={<OrgDashboard />} />
          </Route>

          {/* National Health Intelligence rollup — platform admins only.
              Aggregates Value & Impact across every active org. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/national-dashboard" element={<NationalDashboard />} />
          </Route>

          {/* National Health Intelligence — tabbed population-health
              analytics platform. Platform admins only. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/national" element={<NationalHealthIntelligence />} />
          </Route>

          {/* Audit log viewer — platform admins only. Read-only view
              over AuditLogEntry rows from the clinical audit pipeline. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/audit-log" element={<AuditLogViewer />} />
          </Route>

          {/* Operations dashboard — platform admins only. New patients by
              Customer Type + organisation activity (Phase 1). */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/operations" element={<OperationsDashboard />} />
          </Route>

          {/* Platform Economics (Phase 6.1) — ICER's own running-cost
              capture. Platform admins only. Feeds the future Platform
              Economics section of /superuser/national-dashboard. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/platform-costs" element={<PlatformCostsPage />} />
          </Route>

          {/* Per-org service coverage config (value/billing Phase 2) —
              classify lab/medication/radiology/ambulance as Covered /
              Billable / Off per organisation. Platform admins only. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/service-coverage" element={<ServiceCoverageConfigPage />} />
          </Route>

          {/* Value & Billing statements (value/billing Phase 3) — assemble,
              preview, issue (PDF) and download per-org statements. Platform
              admins only. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/statements" element={<OrgStatementsPage />} />
          </Route>

          {/* Routine medical screening — campaigns, protocols, cohort
              reports, Fitness for Work reports. Platform admins only. */}
          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/screening" element={<ScreeningPage />} />
            <Route path="/superuser/screening/campaigns/:id" element={<ScreeningCampaignDetail />} />
            <Route path="/superuser/triage-queue" element={<TriageQueuePage />} />
          </Route>

          {/* Cost Entry surface (Phase 6.4) — same PlatformCostsPage
              component, but reachable by users in the new
              cost_entry_staff role (plus admins). The cost-entry users
              do NOT see any /superuser/* routes — those stay
              admin-only. RoleHome routes them here automatically. */}
          <Route element={<RequireRole roles={[ROLES.COST_ENTRY, 'admin']} />}>
            <Route path="/cost-entry/platform-costs" element={<PlatformCostsPage />} />
            <Route path="/cost-entry/visits" element={<VisitServiceEntry />} />
          </Route>

          {/* Ambulance */}
          <Route element={<RequireRole roles={[ROLES.AMBULANCE]} />}>
            <Route path="/ambulance" element={<AmbulanceDashboard />} />
            <Route path="/ambulance/requests/:requestId" element={<AmbulanceRequestDetail />} />
            <Route path="/ambulance/catalogue" element={<AmbulanceCatalogue />} />
          </Route>

          <Route element={<RequireRole roles={['admin']} />}>
            <Route path="/superuser/ambulance-catalogue" element={<AmbulanceCatalogue />} />
          </Route>

          {/* Admin — clinician invitations, audit, etc. */}
          <Route element={<RequireRole roles={ROLE_GROUPS.ADMIN} />}>
            <Route path="/admin-invitations" element={<InvitationsPage />} />
            {/* Staff invitations cover the five non-doctor roles
                (pharmacy / lab / radiology / ambulance / org_admin)
                via the polymorphic StaffInvitation backend at
                /api/staff-invitations/. */}
            <Route path="/admin-staff-invitations" element={<StaffInvitationsPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
    <GlobalChat />
    </>
          )        
        }
